import { InputAdornment } from '@mui/material'
import { useRef, useState } from 'react'
import { Controller } from 'react-hook-form'

import {
  UButton,
  UCard,
  UIcon,
  UIconButton,
  UInput,
  ULink,
  UStack,
  UText,
  UFormControlInput,
  UAlert,
} from '@src/components'
import { PATHS } from '@src/consts'
import {
  TurnstileWidget,
  TurnstileWidgetHandle,
  SigninEmailMfa,
} from '@src/features/_components'
import { LoginPageState } from '@src/features/signin/page/signin-main/type'

import {
  useSigninEmailForm,
  useSigninPasswordForm,
  useSignin,
  useSigninEmailMfa,
} from './_hooks'

export const SigninMain = () => {
  const [loginPageState, setLoginPageState] =
    useState<LoginPageState>('INITIAL')
  const [showPassword, setShowPassword] = useState(false)
  const [loginErrors, setLoginErrors] = useState<string[]>([])
  // Bot protection for password login (CRD-4617). This component is UPSIDER
  // only — the PRESIDENT brand renders features/_president/signin instead
  // (branched in pages/signin/index.page.tsx) and is out of scope.
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null)
  const turnstileWidgetRef = useRef<TurnstileWidgetHandle>(null)

  const { signinEmailFormObject } = useSigninEmailForm()
  const { signinPasswordFormObject } = useSigninPasswordForm()

  function handleChangePageState(pageState: LoginPageState) {
    setLoginErrors([])
    setLoginPageState(pageState)
  }

  function handleTurnstileSuccess(token: string) {
    setTurnstileToken(token)
  }

  // Tokens are single-use and expire after ~300s: clear and re-challenge on
  // login failure, widget error, or token expiry.
  function handleResetTurnstile() {
    setTurnstileToken(null)
    turnstileWidgetRef.current?.reset()
  }

  function handleTurnstileScriptLoadError() {
    setLoginErrors([
      'セキュリティ確認の読み込みに失敗しました。ページを再読み込みしてください',
    ])
  }

  const {
    handleSaveRedirectUrl,
    handleLoginWithEmailAndPassword,
    moveToSsoPage,
    isLoadingFetchLoginOptions,
    isLoadingLoginWithEmailAndPassword,
    isLoadingMoveToSsoPage,
  } = useSignin({
    setLoginErrors,
    getEmailFormValue: signinEmailFormObject.getValues,
    getPasswordFormValue: signinPasswordFormObject.getValues,
    handleChangePageState,
    onLoginWithEmailAndPasswordError: handleResetTurnstile,
  })

  const { handleSubmitEmailMfa, isSubmittingEmailMfa } = useSigninEmailMfa({
    setLoginErrors,
  })

  return (
    <>
      {(loginPageState === 'SSO_OR_ID_AND_PASSWORD' ||
        loginPageState === 'ID_AND_PASSWORD' ||
        loginPageState === 'EMAIL_MFA') && (
        <UButton
          startIcon={<UIcon name="arrow_back" color="primary" />}
          color="primary"
          variant="text"
          className="mb-1"
          onClick={() => handleChangePageState('INITIAL')}
        >
          戻る
        </UButton>
      )}
      {loginErrors.length > 0 && (
        <UAlert severity="error" className="mb-2">
          {loginErrors.map((error) => (
            <UText key={error}>{error}</UText>
          ))}
        </UAlert>
      )}
      <UCard className="py-7 px-6">
        {loginPageState === 'INITIAL' && (
          <form
            onSubmit={signinEmailFormObject.handleSubmit(handleSaveRedirectUrl)}
          >
            <Controller
              control={signinEmailFormObject.control}
              name="email"
              render={({ field }) => (
                <UFormControlInput
                  fullWidth
                  error={!!signinEmailFormObject.formState.errors.email}
                  htmlFor="email"
                  required
                  uInputProps={{
                    ...field,
                    helperText:
                      signinEmailFormObject.formState.errors.email?.message,
                    placeholder: 'メールアドレス',
                    InputProps: {
                      autoFocus: true,
                      startAdornment: (
                        <InputAdornment position="start">
                          <UIcon name="email" color="grayDark" />
                        </InputAdornment>
                      ),
                    },
                  }}
                />
              )}
            />
            <UButton
              className="mt-5"
              fullWidth
              size="large"
              color="primary"
              variant="contained"
              type="submit"
              isLoading={isLoadingFetchLoginOptions || isLoadingMoveToSsoPage}
            >
              次へ
            </UButton>
          </form>
        )}
        {loginPageState === 'SSO_OR_ID_AND_PASSWORD' && (
          <>
            <UInput
              fullWidth
              placeholder="メールアドレス"
              value={signinEmailFormObject.getValues().email}
              inputProps={{
                readOnly: true,
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <UIcon name="email" color="grayDark" />
                  </InputAdornment>
                ),
              }}
            />
            <UStack spacing={2} className="mt-5">
              <UButton
                color="primary"
                variant="contained"
                fullWidth
                size="large"
                isLoading={isLoadingMoveToSsoPage}
                onClick={() => moveToSsoPage()}
              >
                シングルサインオンでログイン
              </UButton>
              <UButton
                color="primary"
                fullWidth
                size="large"
                onClick={() => handleChangePageState('ID_AND_PASSWORD')}
              >
                パスワードでログイン
              </UButton>
            </UStack>
          </>
        )}
        {loginPageState === 'ID_AND_PASSWORD' && (
          <form
            onSubmit={signinPasswordFormObject.handleSubmit(() =>
              handleLoginWithEmailAndPassword(turnstileToken)
            )}
          >
            <UInput
              fullWidth
              placeholder="メールアドレス"
              value={signinEmailFormObject.getValues().email}
              inputProps={{ readOnly: true }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <UIcon name="email" color="grayDark" />
                  </InputAdornment>
                ),
                classes: {
                  root: 'mb-3',
                },
              }}
            />
            <Controller
              control={signinPasswordFormObject.control}
              name="password"
              render={({ field }) => (
                <UFormControlInput
                  fullWidth
                  error={!!signinPasswordFormObject.formState.errors.password}
                  htmlFor="password"
                  required
                  uInputProps={{
                    ...field,
                    helperText:
                      signinPasswordFormObject.formState.errors.password
                        ?.message,
                    placeholder: 'パスワード',
                    InputProps: {
                      autoFocus: true,
                      startAdornment: (
                        <InputAdornment position="start">
                          <UIcon name="lock" color="grayDark" />
                        </InputAdornment>
                      ),
                      type: showPassword ? 'text' : 'password',
                      endAdornment: (
                        <InputAdornment position="end">
                          <UIconButton
                            onClick={() => setShowPassword(!showPassword)}
                            iconProps={{
                              name: showPassword
                                ? 'visibility_off'
                                : 'visibility',
                              color: 'grayDark',
                            }}
                            variant="contained"
                          />
                        </InputAdornment>
                      ),
                    },
                  }}
                />
              )}
            />
            <UStack spacing={3} alignItems="center" className="mt-5">
              <TurnstileWidget
                ref={turnstileWidgetRef}
                onSuccess={handleTurnstileSuccess}
                onError={handleResetTurnstile}
                onExpire={handleResetTurnstile}
                onScriptLoadError={handleTurnstileScriptLoadError}
              />
              <UButton
                size="large"
                color="primary"
                variant="contained"
                fullWidth
                type="submit"
                disabled={!turnstileToken}
                isLoading={isLoadingLoginWithEmailAndPassword}
              >
                ログイン
              </UButton>
              <ULink to={PATHS.RESET_PASSWORD} variant="body2" underline="none">
                <UText color="text.secondary" variant="body2">
                  パスワードをお忘れの方
                </UText>
              </ULink>
            </UStack>
          </form>
        )}
        {loginPageState === 'EMAIL_MFA' && (
          <SigninEmailMfa
            email={signinEmailFormObject.getValues().email}
            isSubmitting={isSubmittingEmailMfa}
            onSubmit={handleSubmitEmailMfa}
          />
        )}
      </UCard>
    </>
  )
}
